import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAudioVideo, useRemoteVideoTileState, useRosterState } from 'amazon-chime-sdk-component-library-react'
import { dataURLToBlob } from 'blob-util'
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks'

import { styled, SxProps } from '@mui/system'
import Badge, { BadgeProps } from '@mui/material/Badge'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'

import CameraIcon from '@/components/ui/icons/Camera'
import ControlBarItem from './ControlBarItem'
import _ from 'lodash'

export type ScreenShotControlProps = {
  count?: number
  disabled?: boolean
  onCaptureScreenShot?: (blob: Blob) => void
  sx?: SxProps
}

// TODO: maybe make this as default style for badge component
const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 0,
    top: 4,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}))

export default function ScreenShotControl(props: ScreenShotControlProps) {
  const { count = 0, disabled = false, onCaptureScreenShot, sx } = props
  const audioVideo = useAudioVideo()
  const { attendeeIdToTileId, tileIdToAttendeeId, tiles } = useRemoteVideoTileState()
  const { roster } = useRosterState()
  const popupState = usePopupState({ variant: 'popover', popupId: 'chime-sdk-screenshot-popover' })
  const { t } = useTranslation()

  const captureScreenByAttendee = (attendeeId: string) => {
    if (!audioVideo) return

    const tileId = attendeeIdToTileId[attendeeId]
    const videoTiles = audioVideo.getAllVideoTiles()
    const videoTile = videoTiles.find((videoTile) => videoTile.id() === tileId)
    const imageData = videoTile?.capture()

    if (!imageData) return

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = imageData.width
    canvas.height = imageData.height
    ctx?.putImageData(imageData, 0, 0)

    const imgDataUrl = canvas.toDataURL('image/png')
    const blob = dataURLToBlob(imgDataUrl)
    onCaptureScreenShot?.(blob)
  }

  const buttonDisabled = disabled || !tiles.length

  return (
    <ControlBarItem disableMargin>
      <Tooltip title={<span>{t('component.chimeSDK.screenShotControl.screenShotButton.tooltip')}</span>} arrow>
        {buttonDisabled ? (
          <span>
            <Button disabled variant="neutral" sx={sx}>
              <StyledBadge badgeContent={count} color="primary">
                <CameraIcon />
              </StyledBadge>
            </Button>
          </span>
        ) : (
          <Button variant="neutral" sx={sx} {...bindTrigger(popupState)}>
            <StyledBadge badgeContent={count} color="primary">
              <CameraIcon />
            </StyledBadge>
          </Button>
        )}
      </Tooltip>
      <Menu
        {...bindMenu(popupState)}
        disableAutoFocusItem
        variant="menu"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {Object.values(tileIdToAttendeeId).map((attendeeId) => (
          <MenuItem
            key={attendeeId}
            onClick={() => {
              captureScreenByAttendee(attendeeId)
              bindMenu(popupState).onClose()
            }}
          >
            {_.get(roster, `${attendeeId}.name`, '')}
          </MenuItem>
        ))}
      </Menu>
    </ControlBarItem>
  )
}
